import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Search,
  TrendingUp,
  Clock,
  XCircle,
  Filter
} from 'lucide-react'
import { ApiService } from '@/services/api'
import { format, subDays } from 'date-fns'
import type { Restaurant } from '@/types'

interface ValidationLog {
  _id: string
  restaurantId?: Restaurant
  databaseName: string
  machineId?: string
  appVersion?: string
  ipAddress: string
  userAgent?: string
  result: string
  errorCode?: string
  responseTime: number
  timestamp: Date
}

interface ValidationAnalytics {
  total24h: number
  total7d: number
  total30d: number
  successRate: number
  avgResponseTime: number
  failedBreakdown: Array<{
    code: string
    count: number
  }>
}

export default function ValidationLogsScreen() {
  const [logs, setLogs] = useState<ValidationLog[]>([])
  const [analytics, setAnalytics] = useState<ValidationAnalytics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'))
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [resultFilter, setResultFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')

  // Pagination
  const [currentPage, setCurrentPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const limit = 50

  useEffect(() => {
    loadData()
  }, [currentPage, dateFrom, dateTo, resultFilter])

  const loadData = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const [logsResponse, analyticsResponse] = await Promise.all([
        ApiService.getValidationLogs({
          from: dateFrom ? new Date(dateFrom).toISOString() : undefined,
          to: dateTo ? new Date(dateTo + 'T23:59:59').toISOString() : undefined,
          result: resultFilter !== 'all' ? resultFilter : undefined,
          limit,
          offset: currentPage * limit
        }),
        ApiService.getValidationAnalytics()
      ])

      if (logsResponse.success) {
        setLogs(logsResponse.data.logs)
        setHasMore(logsResponse.data.pagination.hasMore)
      } else {
        setError(logsResponse.message || 'Failed to load validation logs')
      }

      if (analyticsResponse.success) {
        setAnalytics(analyticsResponse.data)
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load validation logs')
    } finally {
      setIsLoading(false)
    }
  }

  const getResultBadge = (result: string) => {
    switch (result) {
      case 'success':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Success</Badge>
      case 'expired':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Expired</Badge>
      case 'not_found':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Not Found</Badge>
      case 'inactive':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Inactive</Badge>
      case 'unauthorized':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Unauthorized</Badge>
      case 'maintenance_mode':
        return <Badge className="bg-purple-100 text-purple-800 border-purple-200">Maintenance</Badge>
      case 'version_too_old':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200">Old Version</Badge>
      default:
        return <Badge variant="outline">{result}</Badge>
    }
  }

  const filteredLogs = logs.filter(log =>
    log.databaseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.restaurantId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.ipAddress.includes(searchTerm) ||
    log.machineId?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Validation Logs</h1>
        <p className="text-muted-foreground mt-2">
          Monitor and analyze subscription validation requests from POS systems
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last 24 Hours</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.total24h.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">validation requests</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Last 7 Days</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.total7d.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">validation requests</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.successRate.toFixed(1)}%</div>
              <p className="text-xs text-muted-foreground">successful validations</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analytics.avgResponseTime}ms</div>
              <p className="text-xs text-muted-foreground">average response</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Failed Validation Breakdown */}
      {analytics && analytics.failedBreakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              Failed Validation Breakdown
            </CardTitle>
            <CardDescription>Most common error codes in the last 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {analytics.failedBreakdown.map((item) => (
                <div key={item.code} className="flex items-center justify-between p-3 border rounded-lg">
                  <span className="text-sm font-medium">{item.code || 'UNKNOWN'}</span>
                  <Badge variant="outline">{item.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="dateFrom">From Date</Label>
              <Input
                id="dateFrom"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dateTo">To Date</Label>
              <Input
                id="dateTo"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="result">Result</Label>
              <Select value={resultFilter} onValueChange={setResultFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Results</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="not_found">Not Found</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="unauthorized">Unauthorized</SelectItem>
                  <SelectItem value="maintenance_mode">Maintenance Mode</SelectItem>
                  <SelectItem value="version_too_old">Version Too Old</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Database, IP, Machine ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Validation Logs</CardTitle>
          <CardDescription>
            Showing {filteredLogs.length} of {logs.length} logs
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No validation logs found
            </div>
          ) : (
            <div className="space-y-2">
              {filteredLogs.map((log) => (
                <div
                  key={log._id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      {getResultBadge(log.result)}
                      <span className="font-medium">
                        {log.restaurantId?.name || log.databaseName}
                      </span>
                      {log.errorCode && (
                        <Badge variant="outline" className="text-xs">
                          {log.errorCode}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>{format(new Date(log.timestamp), 'PPpp')}</span>
                      <span>IP: {log.ipAddress}</span>
                      {log.appVersion && <span>v{log.appVersion}</span>}
                      {log.machineId && <span>Machine: {log.machineId.substring(0, 12)}...</span>}
                      <span className={log.responseTime > 200 ? 'text-amber-600 font-medium' : ''}>
                        {log.responseTime}ms
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {(currentPage > 0 || hasMore) && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0 || isLoading}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage + 1}
              </span>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!hasMore || isLoading}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
